/**
 * Receivables Invoice Link Census -- Phase 1 (read-only).
 *
 * Some "Unpaid" A&L booking rows survive the exact receivables dedup join
 * (services/receivables_dedup.py: external_reference == "{platform}:
 * {booking_id}") even though a real Pennylane invoice exists for them,
 * because that invoice was created manually in Pennylane and never got the
 * reference. This card censuses every non-br_ltd entity's Unpaid bookings
 * against Pennylane invoices with no parseable platform reference, and
 * proposes candidate pairings by currency + amount/name/date proximity.
 *
 * Explicitly heuristic, explicitly read-only -- CLAUDE.md section 6 ("don't
 * auto-correct evidence"): this card never writes. See
 * ReceivablesInvoiceLinkRepairPanel for the gated, row-by-row write path.
 */

import { useState } from 'react';
import { Loader2, Search, Link2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { receivablesInvoiceLinkAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function CandidateRow({ candidate }) {
  return (
    <tr className="border-b border-border/50">
      <td className="py-1 pr-3 font-mono text-xs">{candidate.invoice_id}</td>
      <td className="py-1 pr-3 text-xs">{candidate.supplier_client_name || '--'}</td>
      <td className="py-1 pr-3 text-xs">{candidate.amount} {candidate.currency}</td>
      <td className="py-1 pr-3 text-xs text-muted-foreground">{candidate.issue_date || '--'}</td>
      <td className="py-1 pr-3 text-xs">{Math.round((candidate.name_similarity || 0) * 100)}%</td>
      <td className="py-1 pr-3 text-xs">
        <Badge className={candidate.amount_within_tolerance ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 text-[10px]' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 text-[10px]'}>
          {candidate.amount_within_tolerance ? 'amount ok' : 'amount off'}
        </Badge>
      </td>
    </tr>
  );
}

function BookingCandidates({ booking }) {
  return (
    <div className="border border-border rounded p-3 space-y-2">
      <div className="flex items-center justify-between gap-2 text-xs">
        <p className="font-mono text-muted-foreground">
          {booking.entity_name} -- {booking.platform}:{booking.booking_id}
        </p>
        <span>{booking.guest_name || '(no guest name)'} -- {booking.amount} {booking.currency}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="py-1 pr-3 font-medium text-xs">Invoice</th>
              <th className="py-1 pr-3 font-medium text-xs">Counterparty</th>
              <th className="py-1 pr-3 font-medium text-xs">Amount</th>
              <th className="py-1 pr-3 font-medium text-xs">Issue Date</th>
              <th className="py-1 pr-3 font-medium text-xs">Name Match</th>
              <th className="py-1 pr-3 font-medium text-xs"></th>
            </tr>
          </thead>
          <tbody>
            {booking.candidates.map(function (c) {
              return <CandidateRow key={c.invoice_id} candidate={c} />;
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ReceivablesInvoiceLinkCensusPanel() {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(false);

  var loadCensus = async function () {
    setLoading(true);
    try {
      var res = await receivablesInvoiceLinkAPI.census();
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Link2 className="h-4 w-4" />
          Receivables Invoice Link Census
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Read-only. Pairs "Unpaid" A&amp;L booking rows against Pennylane
          invoices with no parseable "{'{platform}'}:{'{booking_id}'}"
          reference (likely created by hand in Pennylane, so the exact
          dedup join can never see them). Candidate pairing is a heuristic
          (currency match + amount/name/date proximity) for review only --
          this card has no write capability. Use the Receivables Invoice
          Link Repair card below to approve a link.
        </p>

        <Button onClick={loadCensus} disabled={loading}>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Search className="h-4 w-4 mr-2" />
          )}
          Preview
        </Button>

        {data && (
          <div className="space-y-3">
            <p className="text-sm">{data.verdict}</p>
            <div className="flex gap-4 text-xs text-muted-foreground">
              <span>{data.total_unpaid_booking_count} Unpaid booking(s) scanned</span>
              <span>{data.total_unlinked_invoice_count} unlinked invoice(s) scanned</span>
              <span>{data.bookings_with_candidates_count} with candidate(s)</span>
            </div>

            {data.bookings_with_candidates && data.bookings_with_candidates.length > 0 && (
              <div className="space-y-2">
                {data.bookings_with_candidates.map(function (b) {
                  return <BookingCandidates key={b.platform + ':' + b.booking_id} booking={b} />;
                })}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
